import { Input } from 'antd';
import { InputProps } from 'antd/lib/input';
import React, { ReactElement, useEffect, useState } from 'react';
import { convertToValidStr } from '../../../../utils/utility';
import { NON_NAME_REGEX, MAX_CHARACTERS_LIMIT, ONLY_ALPHABET, ONLY_ALPHA_NUMERIC } from '../../../constants/commonConstants';
import { useGetFormConfig } from '../../../hooks/useFormConfig';
import FormItem from '../FormItem/FormItem';

export const FormTextInput = (props: any): (ReactElement | null) => {
  const [extraRules, setExtraRules] = useState<any[]>([]);
  const formConfig = useGetFormConfig(props);
  const innerInputRef = React.useRef<any>(null);
  const {
    label, addonBefore, disabled, onChange: onChangeFromOrigin, form, key, suffixTestId, required, allowClear,
    isName, onlyAlphabet, onlyAlphaNumeric, regex, onBlur, maxLength = false, maxLengthValue, minLengthValue, fieldLengthValue, inputRef,
    onInput, className, isMaxLengthNotApplicable, onInitialize, onFocus, fieldInFocus, ignorePrefixInput,
  } = formConfig;

  const placeholder =
  props?.detail?.placeholder !== undefined
    ? props.detail.placeholder      // Respect the dynamic placeholder from props
    : formConfig?.placeholder;      // Otherwise, fallback to formConfig placeholder

  useEffect((): void => {
    if (onInitialize && form) {
      const initialValue = form.getFieldValue(key);
      onInitialize(initialValue);
    }
  }, []);

  useEffect((): void => {
    if (disabled) {
      setExtraRules([]);
    } else {
      if (form?.getFieldValue(key)) {
        const fieldChangeEvent  = { target: {value: form.getFieldValue(key)} };
        onChange(fieldChangeEvent , label);
      }
      setExtraRules([
        {
          max: maxLengthValue ? fieldLengthValue?.[0] : !isMaxLengthNotApplicable ? (maxLength || MAX_CHARACTERS_LIMIT) : undefined,
        },
        {
          min: minLengthValue ? fieldLengthValue?.[0] : undefined,
          max: maxLengthValue ? fieldLengthValue?.[0] : undefined,
        },
      ]);
    }
  }, [disabled]);

  // Add effect to handle focus when fieldInFocus prop changes
  useEffect((): void => {
    if (fieldInFocus && innerInputRef?.current) {
      innerInputRef.current.focus();
    }
  }, [fieldInFocus]);

  const onChange = (event: any, labelConfig: any): void => {
    const { target: { value } = { value: '' } } = event;
    let validStr = convertToValidStr(value);

    if (addonBefore && ignorePrefixInput) { // to prevent input of the prefix
      const preFixPattern = new RegExp(`^${addonBefore}`, 'i');
      validStr = validStr.replace(preFixPattern, '');
    }

    if (isName) {
      validStr = validStr.replace(NON_NAME_REGEX, '');
    }
    if (onlyAlphabet) {
      validStr = validStr.replace(ONLY_ALPHABET, '');
    }
    if (onlyAlphaNumeric) {
      validStr = validStr.replace(ONLY_ALPHA_NUMERIC, '');
    }
    if (regex) {
      validStr = validStr.replace(regex, '');
    }
    if (form) {
      form.setFieldsValue({ [key]: validStr });
    }
    setExtraRules([
      {
        max: maxLengthValue ? fieldLengthValue?.[0] : !isMaxLengthNotApplicable ? (maxLength || MAX_CHARACTERS_LIMIT) : undefined,
      },
      {
        min: minLengthValue ? fieldLengthValue?.[0] : undefined,
      },
    ]);
    event.target.value = validStr;
    if (onChangeFromOrigin) {
      onChangeFromOrigin(event, labelConfig);
    }
  };

  return <FormItem formConfig={formConfig} extraRules={extraRules}>
    <Input
      allowClear={allowClear}
      data-testid={`${key}${suffixTestId || ''}`}
      prefix={addonBefore}
      placeholder= {placeholder}
      disabled={disabled}
      autoComplete="off"
      onChange={(event: InputProps): void => onChange(event, label)}
      onBlur={onBlur}
      required={required}
      onInput={onInput}
      className={className}
      ref={(node: any): void => {
        innerInputRef.current = node;
        if (inputRef) {
          typeof inputRef === 'function' ? inputRef(node) : (inputRef.current = node);
        }
      }}
      onFocus={(event: React.FocusEvent<HTMLInputElement>): void => {
        onFocus?.(event);
      }}
    />
  </FormItem>;
};

export default FormTextInput;
